import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api.ts';
import { SiteLayout } from '../../components/SiteLayout.tsx';

export function RequestPage() {
  const [params] = useSearchParams();
  const foreninger = useQuery({ queryKey: ['foreninger'], queryFn: () => api.listForeninger() });

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [foreningId, setForeningId] = useState(params.get('klubb') ?? '');
  const [sailNumber, setSailNumber] = useState('');
  const [boatName, setBoatName] = useState('');
  const [message, setMessage] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState<'pending' | 'exists' | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    try {
      const res = await api.kontoForesporsel({
        fullName,
        email,
        foreningId,
        sailNumber: sailNumber.trim() || null,
        boatName: boatName.trim() || null,
        message: message.trim() || null,
      });
      setDone(res.status);
    } catch (error) {
      setErr((error as ApiError).message);
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <Shell>
        <div className="text-center">
          <div className="text-4xl mb-3">{done === 'exists' ? '👤' : '✅'}</div>
          {done === 'exists' ? (
            <>
              <h1 className="text-lg font-semibold mb-2">Det finnes allerede en konto</h1>
              <p className="text-sm text-slate-400 mb-5">
                E-posten er allerede registrert. Logg inn i stedet.
              </p>
              <Link
                to="/logg-inn"
                className="inline-block bg-sky-600 hover:bg-sky-500 px-5 py-2.5 rounded-lg font-semibold"
              >
                Til innlogging
              </Link>
            </>
          ) : (
            <>
              <h1 className="text-lg font-semibold mb-2">Forespørselen er sendt</h1>
              <p className="text-sm text-slate-400 mb-5">
                Kontoen din godkjennes manuelt. Du får en e-post med lenke for å sette passord når
                den er klar.
              </p>
              <Link to="/" className="text-sm text-sky-400 hover:text-sky-300">
                ← Til forsiden
              </Link>
            </>
          )}
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <form onSubmit={submit} className="space-y-3">
        <div className="text-center mb-2">
          <h1 className="text-xl font-bold">Be om konto</h1>
          <p className="text-xs text-slate-400 mt-1">
            Kontoen godkjennes manuelt før du kan logge inn.
          </p>
        </div>
        {err && (
          <div className="text-red-300 bg-red-900/30 border border-red-800 rounded-lg p-3 text-sm">
            {err}
          </div>
        )}
        <Field label="Navn">
          <input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            autoComplete="name"
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          />
        </Field>
        <Field label="E-post">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          />
        </Field>
        <Field label="Klubb">
          <select
            value={foreningId}
            onChange={(e) => setForeningId(e.target.value)}
            required
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          >
            <option value="">— Velg klubb —</option>
            {(foreninger.data ?? []).map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Seilnummer (valgfritt)">
          <input
            value={sailNumber}
            onChange={(e) => setSailNumber(e.target.value.toUpperCase())}
            placeholder="f.eks. GJ123"
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg font-mono"
          />
        </Field>
        <Field label="Båtnavn (valgfritt)">
          <input
            value={boatName}
            onChange={(e) => setBoatName(e.target.value)}
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          />
        </Field>
        <Field label="Melding (valgfritt)">
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          />
        </Field>
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-sky-600 hover:bg-sky-500 py-3 rounded-lg font-semibold disabled:opacity-60"
        >
          {loading ? 'Sender…' : 'Send forespørsel'}
        </button>
        <div className="text-center">
          <Link to="/logg-inn" className="text-sm text-slate-400 hover:text-slate-200">
            Har du konto? Logg inn
          </Link>
        </div>
      </form>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <SiteLayout>
      <main className="flex justify-center px-4 py-12 sm:py-16">
        <div className="w-full max-w-sm bg-slate-900/60 border border-slate-800 rounded-2xl p-6">
          {children}
        </div>
      </main>
    </SiteLayout>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      {children}
    </div>
  );
}
